import { supabaseService } from '../db/supabase';
import { authService } from '../services/auth';
import { listarCampus } from '../data/campus';
import { UIComponents } from '../ui/components';
import type { AppState, PapelUsuario, UsuarioAcesso } from '../types';

const byId = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;

export class ModuloUsuarios {
  constructor(private state: AppState) {}

  inicializar() {
    this.prepararFormulario();
    this.vincularEventos();
    void this.carregarUsuarios();
  }

  private prepararFormulario() {
    const papel = byId<HTMLSelectElement>('usuario-papel');
    const campus = byId('usuario-campus-lista');
    if (!papel || !campus) return;

    const opcoes: PapelUsuario[] = this.state.session?.papel === 'admin'
      ? ['gestor', 'inventariante', 'admin']
      : ['inventariante'];

    papel.innerHTML = opcoes.map(item => `<option value="${item}">${this.labelPapel(item)}</option>`).join('');
    campus.innerHTML = listarCampus().map(item => {
      const disabled = !authService.canAccessCampus(this.state.session, item.sigla) ? 'disabled' : '';
      const checked = this.state.session?.papel !== 'admin' && this.state.session?.campusSiglas.includes(item.sigla) ? 'checked' : '';
      return `<label class="check-card ${disabled ? 'disabled' : ''}"><input type="checkbox" value="${item.sigla}" ${checked} ${disabled}> ${item.sigla} · ${item.nome}</label>`;
    }).join('');
  }

  private async carregarUsuarios() {
    const lista = byId('usuarios-lista');
    if (!lista) return;

    lista.textContent = 'Carregando usuários...';

    try {
      const usuarios = await supabaseService.listarUsuarios();
      const visiveis = usuarios.filter(usuario => this.usuarioVisivel(usuario));

      if (!visiveis.length) {
        lista.textContent = 'Nenhum usuário encontrado.';
        return;
      }

      lista.innerHTML = visiveis.map(usuario => `
        <div class="list-item user-item">
          <div>
            <strong>${usuario.nome || usuario.email}</strong><br>
            <small>${usuario.email}</small><br>
            <small>${this.labelPapel(usuario.papel)} · ${usuario.pode_ver_todos_campus ? 'Todos os campus' : (usuario.campus_siglas || []).join(', ')}</small>
          </div>
          <span class="badge ${usuario.ativo ? 'badge-green' : 'badge-red'}">${usuario.ativo ? 'Ativo' : 'Inativo'}</span>
        </div>
      `).join('');
    } catch (error) {
      lista.textContent = `Erro: ${(error as Error).message}`;
    }
  }

  private usuarioVisivel(usuario: UsuarioAcesso) {
    if (this.state.session?.papel === 'admin') return true;
    if (this.state.session?.papel !== 'gestor') return false;
    if (usuario.papel !== 'inventariante') return false;
    return (usuario.campus_siglas || []).some(sigla => this.state.session?.campusSiglas.includes(sigla));
  }

  private async criarUsuario() {
    const email = byId<HTMLInputElement>('usuario-email')?.value.trim();
    const nome = byId<HTMLInputElement>('usuario-nome')?.value.trim() || '';
    const password = byId<HTMLInputElement>('usuario-senha')?.value;
    const papel = byId<HTMLSelectElement>('usuario-papel')?.value as PapelUsuario;
    const todos = byId<HTMLInputElement>('usuario-todos-campus')?.checked || false;
    const campusSiglas = Array.from(document.querySelectorAll<HTMLInputElement>('#usuario-campus-lista input:checked')).map(input => input.value);

    if (!email || !password || !papel) {
      UIComponents.warning('Informe e-mail, senha inicial e perfil.');
      return;
    }

    if (!authService.canCreateRole(this.state.session, papel)) {
      UIComponents.error('Seu perfil não pode criar esse tipo de usuário.');
      return;
    }

    if (!todos && !campusSiglas.length) {
      UIComponents.warning('Selecione ao menos um campus.');
      return;
    }

    const loading = UIComponents.showLoading('Criando usuário...');
    try {
      await supabaseService.criarUsuario({
        email: email.includes('@') ? email : `${email}@inventariar.utfpr`,
        password,
        nome,
        papel,
        campus_siglas: todos ? [] : campusSiglas,
        pode_ver_todos_campus: this.state.session?.papel === 'admin' && todos,
      });
      UIComponents.hideLoading(loading);
      UIComponents.success('Usuário criado com sucesso!');
      this.limparFormulario();
      await this.carregarUsuarios();
    } catch (error) {
      UIComponents.hideLoading(loading);
      UIComponents.error((error as Error).message);
    }
  }

  private limparFormulario() {
    ['usuario-email', 'usuario-nome', 'usuario-senha'].forEach(id => {
      const input = byId<HTMLInputElement>(id);
      if (input) input.value = '';
    });
    const todos = byId<HTMLInputElement>('usuario-todos-campus');
    if (todos) todos.checked = false;
    this.prepararFormulario();
  }

  private labelPapel(papel: PapelUsuario) {
    return {
      admin: 'Admin',
      gestor: 'Gestor',
      inventariante: 'Inventariante',
    }[papel];
  }

  private vincularEventos() {
    byId('btn-criar-usuario')?.addEventListener('click', () => this.criarUsuario());
    byId('usuario-todos-campus')?.addEventListener('change', () => {
      const checked = byId<HTMLInputElement>('usuario-todos-campus')?.checked || false;
      document.querySelectorAll<HTMLInputElement>('#usuario-campus-lista input').forEach(input => {
        if (this.state.session?.papel === 'admin') input.disabled = checked;
      });
    });
  }
}
